import numeral from "numeral"
import { SERIE_FORMAT } from "../feature/chart/ChartOptions"
import { formatTime } from "./date"
import { canUseNumeral } from "./numerals"

type Value = string | number | undefined | null

/**
 * Defines how numbers are abbreviated when displayed
 * - `"compact"` → `1M`
 * - `"consistent"` → `1,000 K`
 * - `"full"` → `1,000,000`
 */
export type NumberAbbreviationFormat = "compact" | "full" | "consistent"

export const isAbbreviationFormat = (
  format: string
): format is NumberAbbreviationFormat => {
  return format === "compact" || format === "full" || format === "consistent"
}

type FormatterParams = {
  value: Value
  /** The Numeral.js format string to apply (See http://numeraljs.com/#format) */
  format?: string
  abbreviationFormat?: NumberAbbreviationFormat
  prefix?: string
  /** Number of decimal places to show (overrides format if provided) */
  decimals?: number
}

export function percentageFormatter({
  value,
  abbreviationFormat = "full",
  prefix = "",
  decimals = 1,
}: FormatterParams): string {
  if (value === null || value === undefined) {
    return ""
  }

  const numValue = Number(value)

  const scaled = numValue * 100
  const absScaled = Math.abs(scaled)
  if (absScaled < 0.1 && absScaled > 0) {
    const sign = scaled < 0 ? "-" : ""
    return `${prefix}${sign}0.${"0".repeat(Math.max(decimals - 1, 0))}1%`
  }

  const decimalFormat = `0,0.${"0".repeat(decimals)}`

  if (abbreviationFormat === "full") {
    return `${prefix}${numeral(scaled).format(decimalFormat)}%`
  } else {
    return `${prefix}${numeral(numberFormatter({ value: scaled, abbreviationFormat, decimals })).format(decimalFormat)}%`
  }
}

/**
 * Convert a fraction (0.05) to display scale (5) for numeric uses where
 * the caller needs a number, not a formatted string — chart series data,
 * threshold comparisons, etc. For formatting, pass the raw fraction to
 * percentageFormatter / formatNumber({ metricFormat: "percent" }) instead.
 */
export const toDisplayPercent = (value: number): number => value * 100

/** Inverse of toDisplayPercent — for form inputs that store fractions. */
export const fromDisplayPercent = (value: number): number => value / 100

export function ratioFormatter({
  value,
  prefix,
  decimals = 1,
}: FormatterParams) {
  if (value === null || value === undefined) {
    return ""
  }
  const decimalFormat = `0,0.${"0".repeat(decimals)}`
  return `${prefix || ""}${numeral(value).format(decimalFormat)}x`
}

export function numberFormatter({
  value,
  abbreviationFormat,
  format,
  prefix = "",
  decimals,
}: FormatterParams) {
  if (value === null || value === undefined) {
    return ""
  }

  let compactFormat = "0.0 a"
  let fullFormat = "0,0"

  // For very small values (< 0.01), show up to 5 decimal places
  // For small values (0.01–1), show up to 4 decimal places
  if (Number(value) < 0.01) {
    compactFormat =
      decimals !== undefined ? `0.${"0".repeat(decimals)} a` : `0.00[000] a`
    fullFormat =
      decimals !== undefined ? `0.${"0".repeat(decimals)}` : `0.00[000]`
  } else if (Number(value) < 1) {
    compactFormat =
      decimals !== undefined ? `0.${"0".repeat(decimals)} a` : `0.00[00] a`
    fullFormat =
      decimals !== undefined ? `0.${"0".repeat(decimals)}` : `0.00[00]`
  } else if (decimals !== undefined) {
    compactFormat = `0.${"0".repeat(decimals)} a`
    fullFormat = `0,0.${"0".repeat(decimals)}`
  }

  if (abbreviationFormat === "compact") {
    const valueFormatted = numeral(value)
      .format(`${prefix}${compactFormat}`)
      .toUpperCase()
    if (valueFormatted.includes("NAN")) {
      return numeral(Number(value) * 1000000)
        .format(`${prefix}${compactFormat}`)
        .replace("0.0", "0.000000")
        .toUpperCase()
    }
    return valueFormatted
  } else if (abbreviationFormat === "consistent") {
    const valueInThousands = Number(value) / 1000
    let consistentFormat
    if (decimals !== undefined) {
      consistentFormat =
        valueInThousands < 10
          ? `${prefix}0,0.${"0".repeat(decimals)}`
          : `${prefix}0,0`
    } else {
      consistentFormat =
        valueInThousands < 10 ? `${prefix}0,0.000` : `${prefix}0,0`
    }
    return `${numeral(valueInThousands).format(consistentFormat)} K`
  } else if (abbreviationFormat === "full") {
    return numeral(value).format(`${prefix}${fullFormat}`).toUpperCase()
  }

  return numeral(value).format(format).toUpperCase()
}

export function bytesFormatter({
  value,
  prefix,
  decimals = 1,
}: FormatterParams) {
  if (value === null || value === undefined) {
    return ""
  }
  const decimalFormat = `0[.]${"0".repeat(decimals)} b`
  return `${prefix || ""}${numeral(value).format(decimalFormat)}`
}

export function timeFormatter({ value, prefix }: FormatterParams) {
  if (typeof value === "undefined" || value === null) {
    return "-"
  }
  if (typeof value === "string") {
    // Should never be :thinking:
    return `${prefix || ""}${formatTime(Number.parseInt(value, 10))}`
  }
  return `${prefix || ""}${formatTime(value)}`
}

function pricingFormatterWithDecimals({
  value,
  format,
  prefix,
  decimals,
}: FormatterParams) {
  if (!value) {
    return `0`
  }
  if (canUseNumeral(value)) {
    if (format) {
      return numeral(value).format(format)
    }

    if (decimals !== undefined) {
      const decimalFormat = `$0.${"0".repeat(decimals)} a`
      return numeral(value).format(decimalFormat)
    }

    return numeral(value).format(
      Number(value) > 0.01 ? "$0.00 a" : "$0,0.00[000] a"
    )
  }
  return `${prefix || "$"}${value}`
}

export const formatters = {
  [SERIE_FORMAT.PRICE]: ({
    value,
    abbreviationFormat,
    format,
    prefix,
    decimals,
  }: FormatterParams) => {
    if (
      abbreviationFormat === "compact" ||
      abbreviationFormat === "full" ||
      abbreviationFormat === "consistent"
    ) {
      return numberFormatter({
        value,
        format,
        prefix: prefix || "$",
        decimals,
        abbreviationFormat,
      })
    }
    return pricingFormatterWithDecimals({
      value,
      format,
      prefix,
      decimals,
      abbreviationFormat,
    })
  },
  [SERIE_FORMAT.RATIO]: ratioFormatter,
  [SERIE_FORMAT.PERCENTAGE]: percentageFormatter,
  [SERIE_FORMAT.NUMBER]: numberFormatter,
  [SERIE_FORMAT.TOKEN]: numberFormatter,
  [SERIE_FORMAT.TIME]: timeFormatter,
  [SERIE_FORMAT.BYTES]: bytesFormatter,
  [SERIE_FORMAT.TOKEN_ICP]: numberFormatter,
  [SERIE_FORMAT.TOKEN_CKBTC]: numberFormatter,
  [SERIE_FORMAT.TOKEN_CKETH]: numberFormatter,
  [SERIE_FORMAT.CYCLES_PER_SECONDS]: numberFormatter,
  [SERIE_FORMAT.KWH]: numberFormatter,
} as const

export function formatNumber({
  value,
  abbreviationFormat,
  metricFormat,
  prefix,
  decimals,
}: {
  value: Value
  abbreviationFormat?: "compact" | "full" | "consistent"
  metricFormat?: string
  prefix?: string
  decimals?: number
}) {
  const formatter = formatters[metricFormat as SERIE_FORMAT]
  if (formatter) {
    return formatter({ value, abbreviationFormat, prefix, decimals })
  }
  return numberFormatter({ value, abbreviationFormat, prefix, decimals })
}

/**
 * Integer count: thousand-separated, no decimals (`1,234`), em dash (`—`) when
 * missing. Use for count tiles and pagination instead of hand-rolling
 * `formatNumber({ value, abbreviationFormat: "full", decimals: 0 })`.
 */
export const formatCount = (value: number | null | undefined): string =>
  value == null
    ? "—"
    : formatNumber({ value, abbreviationFormat: "full", decimals: 0 })
